// Reads the QR code of a receipt photo in the browser (ZATCA receipts carry one: seller, VAT number,
// date, total, VAT). Uses the same zxing library as the warehouse scanner. A PDF, or a photo whose code
// cannot be read (thermal receipts fade), answers null and the fields are typed instead.
import { BrowserQRCodeReader } from '@zxing/browser';
import { DecodeHintType } from '@zxing/library';

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The image could not be read'));
    img.src = url;
  });
}

/** The image drawn at most `maxSide` pixels on its longer side. */
function toCanvas(img: HTMLImageElement, maxSide: number): HTMLCanvasElement {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const g = canvas.getContext('2d');
  if (g) g.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** The text of the first QR code found on the photo, or null. */
export async function readQrFromImage(file: File): Promise<string | null> {
  if (!file || !file.type.startsWith('image/')) return null;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserQRCodeReader(hints);
    // Large phone photos first at a moderate size (faster), then smaller, then full size.
    for (const side of [2000, 1200, 4000]) {
      try {
        return reader.decodeFromCanvas(toCanvas(img, side)).getText();
      } catch {
        // not found at this size
      }
    }
    return null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
